import React, { useState } from 'react'

import { ArtboardDetailInfoFragment, VersionFragment } from '@sketch/gql-types'

import { ArtboardAnnotations } from 'modules/annotations/containers'
import {
  createTabs,
  useShareSidebarTab,
} from 'modules/shares/components/ShareSidebarTabContext'
import SidebarHeader from 'modules/shares/components/SidebarHeader'
import { ShareWithoutVersion, useVersioning } from 'modules/versioning'
import { ArtboardRevisions } from 'modules/versioning/components'

import { ArtboardDetailInspector } from '../ArtboardDetailInspector'
import { SidebarWrapper, Wrapper } from './ArtboardDetailSidebar.styles'

interface ArtboardDetailSidebarProps {
  share: ShareWithoutVersion
  artboard: ArtboardDetailInfoFragment
  currentVersion: VersionFragment | undefined
  userCanInspect: boolean
  toggleSidebar: () => void
  isViewingLatestRevision: boolean
}

export const ArtboardDetailSidebar: React.FC<ArtboardDetailSidebarProps> = ({
  share,
  artboard,
  userCanInspect,
  toggleSidebar,
  isViewingLatestRevision,
  currentVersion,
}) => {
  // Check which tabs the user has access
  const [segments] = useState(
    createTabs({
      shouldShowComment: true,
      shouldShowInspector: userCanInspect,
      shouldShowVersion: true,
    })
  )
  const [activeSegment, setActiveSegment] = useShareSidebarTab(segments)
  const { isViewingLatestVersion } = useVersioning()

  // Render the selected tab
  let tab = null
  switch (activeSegment) {
    case 'Comment': {
      tab = (
        <ArtboardAnnotations
          share={share}
          permanentArtboardIdentifier={artboard.uuid}
          permanentArtboardShortId={artboard.permanentArtboardShortId || ''}
          permanentPageIdentifier={artboard.page?.uuid || ''}
          artboardNotificationStatus={artboard.subscriptionStatus}
          isViewingLatestVersion={
            isViewingLatestRevision && isViewingLatestVersion
          }
        />
      )
      break
    }
    case 'Version': {
      tab = (
        <ArtboardRevisions
          shareIdentifier={share.identifier}
          permanentArtboardIdentifier={artboard.uuid}
          permanentArtboardShortId={artboard.permanentArtboardShortId || ''}
          artboardRevisionIdentifier={artboard.revisionIdentifier || ''}
        />
      )
      break
    }
    case 'Inspect': {
      tab = (
        <ArtboardDetailInspector
          share={share}
          artboard={artboard}
          currentVersion={currentVersion}
        />
      )
      break
    }
  }

  return (
    <Wrapper>
      <SidebarHeader
        segments={segments}
        currentValue={activeSegment}
        setValue={setActiveSegment}
        toggleSidebar={toggleSidebar}
      />
      {tab && <SidebarWrapper>{tab}</SidebarWrapper>}
    </Wrapper>
  )
}

/**
 * Renders an empty placeholder for the sidebar.
 */
export function ArtboardDetailSidebarPlaceholder() {
  return <Wrapper />
}
